// src/pages/Categories/index.tsx
import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import CategoryForm from './components/CategoryForm'
import CategoryList from './components/CategoryList'
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory
} from '../../services/CategoryService'
import type { Category } from '../../types/@types/category'
import { Tag } from 'lucide-react'

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      const data = await getCategories()
      setCategories(data)
    } catch {
      toast.error('Error al cargar las categorías')
    }
  }

  const handleSubmit = async (category: Omit<Category, 'id'>) => {
    try {
      if (currentCategory) {
        await updateCategory(currentCategory.id, category)
        toast.success('Categoría actualizada con éxito')
      } else {
        await createCategory(category)
        toast.success('Categoría creada con éxito')
      }
      setCurrentCategory(null)
      fetchCategories()
    } catch {
      toast.error('Error al guardar la categoría')
    }
  }

  const handleEdit = (category: Category) => {
    setCurrentCategory(category)
    // Scroll to top on mobile so form is visible
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: string) => {
    try {
      const message = await deleteCategory(id)
      setCategories((prev) => prev.filter((category) => category.id !== id))
      toast.success(message)
    } catch {
      toast.error('Error al eliminar la categoría')
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Tag className="text-primary" size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text">Categorías</h1>
          <p className="text-sm text-text-muted">Gestiona las categorías del menú</p>
        </div>
      </div>

      {/* Two-column on md+, single column on mobile */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Form */}
        <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-text-muted">
            {currentCategory ? 'Editar categoría' : 'Nueva categoría'}
          </h2>
          <CategoryForm category={currentCategory} onSubmit={handleSubmit} onCancel={() => setCurrentCategory(null)} />
        </div>

        {/* List */}
        <div className="rounded-2xl border border-border bg-surface shadow-sm overflow-hidden">
          <div className="border-b border-border px-5 py-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">
              Categorías ({categories.length})
            </h2>
          </div>
          <CategoryList categories={categories} onEdit={handleEdit} onDelete={handleDelete} />
        </div>
      </div>
    </div>
  )
}

export default Categories
